import { useEffect, useState } from "react";
import { api, idr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import InputRupiah from "../components/InputRupiah";
import {
  PackagePlus, Plus, X, Download, CheckCircle2, XCircle, RotateCcw,
  Clock, Loader2, Settings2, Pencil, Trash2, Check,
} from "lucide-react";
import LoadingState from "@/components/ui/loading-state";
import { formatTanggal } from "@/lib/tanggal";

const STATUS_META = {
  aktif: { label: "Aktif", chip: "bg-amber-100 text-amber-800", icon: Clock },
  selesai: { label: "Selesai", chip: "bg-emerald-100 text-emerald-800", icon: CheckCircle2 },
  batal: { label: "Batal", chip: "bg-slate-100 text-slate-500", icon: XCircle },
};

const STATUSES = ["aktif", "selesai", "batal"];

const emptyForm = () => ({
  item_id: "", nama_pelanggan: "", no_hp: "", dp: false, dp_nominal: 0, catatan: "", cabang: "",
  // Brief owner 29 Agu poin 1b-1c.
  type_dipilih: "", harga: 0, trade_in_nama: "", trade_in_harga: 0,
});

const fmtWaktu = (iso) => {
  if (!iso || iso === "-") return "-";
  return formatTanggal(iso, { denganJam: true });
};

export default function PreOrder() {
  const { can } = useAuth();
  const bolehLihat = can("preorder", "view");
  const bolehTambah = can("preorder", "create");
  const bolehKelola = can("preorder", "update"); // status entri + CRUD katalog

  const [items, setItems] = useState([]);
  const [grup, setGrup] = useState([]);
  const [katalog, setKatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterItem, setFilterItem] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [branches, setBranches] = useState([]);
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [form, setForm] = useState(null);

  const loadEntri = async (itemId = filterItem, status = filterStatus) => {
    setLoading(true);
    try {
      const params = {};
      if (itemId) params.item_id = itemId;
      if (status) params.status = status;
      const r = await api.get("/preorder", { params });
      setItems(r.data.items || []);
      // Pengelompokan & rekap dihitung SERVER — daftarnya dipotong di server,
      // jadi menghitungnya di layar akan melewatkan baris yang tidak terkirim.
      setGrup(r.data.grup || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat daftar pre-order");
    } finally { setLoading(false); }
  };

  const loadKatalog = () => api.get("/preorder/items")
    .then(r => setKatalog(r.data.items || []))
    .catch(e => toast.error(e?.response?.data?.detail || "Gagal memuat katalog barang PO"));

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadEntri(); loadKatalog(); }, [filterItem, filterStatus]);

  useEffect(() => {
    api.get("/finance/branches/master")
      .then(r => setBranches((r.data.items || []).map(b => b.name)))
      .catch(() => {});
  }, []);

  const katalogAktif = katalog.filter(k => k.aktif !== false);
  // Opsi type milik katalog yang sedang dipilih di formulir. Dibaca dari
  // katalognya, bukan disalin ke form, supaya perubahan katalog langsung
  // terlihat tanpa perlu menutup-buka formulir.
  const opsiKatalogTerpilih =
    (katalog.find(k => k.id === (form?.item_id || "")) || {}).opsi_type || [];
  const namaBarang = (id) => katalog.find(k => k.id === id)?.nama || "-";

  const ubahStatus = async (it, status) => {
    let alasan = "";
    if (status === "batal") {
      alasan = window.prompt(`Alasan membatalkan pre-order ${it.nama_pelanggan}?`) || "";
      if (!alasan.trim()) return;
    }
    setBusy(true);
    try {
      const r = await api.patch(`/preorder/${it.id}/status`, { status, alasan });
      toast.success(r.data.message || "Status diubah");
      await loadEntri();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mengubah status");
    } finally { setBusy(false); }
  };

  const simpanEntri = async () => {
    if (!form.item_id) { toast.error("Pilih barang PO dari katalog dulu"); return; }
    if (!form.nama_pelanggan.trim()) { toast.error("Nama pelanggan wajib diisi"); return; }
    if (opsiKatalogTerpilih.length > 0 && !form.type_dipilih) {
      toast.error("Pilih dulu type-nya"); return;
    }
    if (form.dp && (!form.dp_nominal || form.dp_nominal <= 0)) {
      toast.error("Nominal DP wajib diisi (lebih dari 0) kalau checkbox DP dicentang");
      return;
    }
    setBusy(true);
    try {
      const body = { ...form, dp_nominal: form.dp ? form.dp_nominal : 0 };
      const r = await api.post("/preorder", body);
      toast.success(r.data.message || "Pre-order tercatat");
      if (r.data.catatan_uang) {
        toast.info(r.data.catatan_uang, { duration: 10000 });
      }
      setForm(null);
      await loadEntri();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan pre-order");
    } finally { setBusy(false); }
  };

  const exportExcel = async () => {
    setExporting(true);
    try {
      const params = {};
      if (filterItem) params.item_id = filterItem;
      if (filterStatus) params.status = filterStatus;
      const res = await api.get("/preorder/export", { params, responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const labelBarang = filterItem ? namaBarang(filterItem).replace(/[^\w-]+/g, "_") : "semua";
      a.download = `PreOrder_${labelBarang}${filterStatus ? `_${filterStatus}` : ""}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mengunduh Excel pre-order");
    } finally { setExporting(false); }
  };

  return (
    <div className="p-4 md:p-8 space-y-5" data-testid="pre-order">
      <div className="flex items-start justify-between flex-col lg:flex-row gap-3">
        <div>
          <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">POS &amp; Sales</p>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mt-1 flex items-center gap-2">
            <PackagePlus className="w-7 h-7 text-emerald-600" /> List Pre-Order (DP)
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Pelanggan yang sudah pesan/DP barang yang belum masuk. Nominal DP di sini hanya
            catatan penjejakan — uangnya tetap wajib dicatat manual di Finance &gt; Catat Cepat.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {bolehLihat && (
            <button onClick={exportExcel} disabled={exporting} data-testid="po-export"
                    className="h-10 px-4 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-sm font-medium text-slate-700 inline-flex items-center gap-2 disabled:opacity-50">
              {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Export Excel
            </button>
          )}
          {bolehTambah && (
            <button onClick={() => setForm(emptyForm())} data-testid="po-tambah"
                    className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium inline-flex items-center gap-2">
              <Plus className="w-4 h-4" /> Tambah Pre-Order
            </button>
          )}
        </div>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-2 flex-wrap">
        <select value={filterItem} onChange={e => setFilterItem(e.target.value)} data-testid="po-filter-item"
                className="h-9 px-3 rounded-lg border border-slate-200 text-sm bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500">
          <option value="">Semua Barang</option>
          {katalog.map(k => <option key={k.id} value={k.id}>{k.nama}{k.aktif === false ? " (nonaktif)" : ""}</option>)}
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} data-testid="po-filter-status"
                className="h-9 px-3 rounded-lg border border-slate-200 text-sm bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500">
          <option value="">Semua Status</option>
          {STATUSES.map(s => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
        </select>
      </div>

      {/* Daftar entri */}
      <div className="space-y-2">
        <LoadingState
          loading={loading}
          kosong={!loading && items.length === 0}
          pesanKosong="Belum ada entri pre-order."
          data-testid="po-loading"
        >
        {!loading && grup.map(g => (
          <div key={g.item_id} data-testid={`po-grup-${g.item_id}`}
               className="rounded-xl border border-slate-200 bg-slate-50/60 p-3 space-y-2">
            {/* Kepala kelompok: judul katalog + rekap per opsi (poin 1d-1f).
                Rekapnya menghitung PO yang masih AKTIF saja, jadi angkanya
                berkurang sendiri begitu satu PO selesai atau dibatalkan. */}
            <div className="flex items-start justify-between gap-3 flex-wrap px-1">
              <div className="min-w-0">
                <p className="font-display font-bold text-sm text-slate-900">{g.judul}</p>
                <p className="text-[11px] text-slate-500">
                  {g.aktif} aktif dari {g.jumlah} pre-order
                  {g.total_dp > 0 && <> · DP terkumpul {idr(g.total_dp)}</>}
                </p>
              </div>
              <div className="flex items-center gap-1.5 flex-wrap justify-end">
                {Object.entries(g.rekap_opsi || {})
                  .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
                  .map(([opsi, n]) => (
                    <span key={opsi} data-testid={`po-rekap-${g.item_id}-${opsi}`}
                          className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${
                            n > 0 ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-400"}`}>
                      {opsi}: {n}
                    </span>
                  ))}
              </div>
            </div>
        {g.items.map(it => {
          const meta = STATUS_META[it.status] || STATUS_META.aktif;
          const Icon = meta.icon;
          return (
            <div key={it.id} data-testid={`po-row-${it.id}`}
                 className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full ${meta.chip}`}>
                      <Icon className="w-3 h-3" /> {meta.label}
                    </span>
                    {it.dp ? (
                      <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                        DP {idr(it.dp_nominal)}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">
                        Tanpa DP
                      </span>
                    )}
                    <span className="font-mono text-[11px] text-slate-400">{it.code}</span>
                  </div>
                  <p className="text-sm font-semibold text-slate-900 mt-1.5">
                    {it.nama_pelanggan}
                    {it.no_hp && <span className="font-normal text-slate-500"> · {it.no_hp}</span>}
                  </p>
                  <p className="text-sm text-slate-700 mt-0.5">
                    {it.nama}
                    {it.type_dipilih && (
                      <span className="ml-1.5 text-[11px] px-1.5 py-0.5 rounded bg-sky-100 text-sky-800 font-medium">
                        {it.type_dipilih}
                      </span>
                    )}
                    {it.cabang && <span className="text-xs text-slate-500"> — {it.cabang}</span>}
                  </p>
                  {(it.harga > 0 || it.trade_in_nama) && (
                    <p className="text-xs text-slate-600 mt-0.5">
                      {it.harga > 0 && <>Harga {idr(it.harga)}</>}
                      {it.trade_in_nama && (
                        <span className="text-violet-700">
                          {it.harga > 0 ? " · " : ""}Trade-in: {it.trade_in_nama}
                          {it.trade_in_harga > 0 && <> ({idr(it.trade_in_harga)})</>}
                        </span>
                      )}
                    </p>
                  )}
                  {it.status === "batal" && it.batal_alasan && (
                    <p className="text-xs text-rose-600 mt-1">Alasan batal: {it.batal_alasan}</p>
                  )}
                  {it.catatan && <p className="text-xs text-slate-500 mt-1">Catatan: {it.catatan}</p>}
                  <p className="text-[11px] text-slate-400 mt-1.5">
                    Diinput oleh <span className="font-medium text-slate-600">{it.created_by_name || it.created_by || "-"}</span>
                    {" · "}{fmtWaktu(it.created_at)}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {bolehKelola && it.status === "aktif" && (
                    <>
                      <button onClick={() => ubahStatus(it, "selesai")} disabled={busy}
                              data-testid={`po-selesai-${it.id}`}
                              className="h-9 px-3 rounded-lg border border-sky-300 text-sky-700 hover:bg-sky-50 text-sm font-medium inline-flex items-center gap-1.5 disabled:opacity-50">
                        <CheckCircle2 className="w-4 h-4" /> Selesai
                      </button>
                      <button onClick={() => ubahStatus(it, "batal")} disabled={busy}
                              data-testid={`po-batal-${it.id}`}
                              className="h-9 px-3 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 text-sm font-medium inline-flex items-center gap-1.5 disabled:opacity-50">
                        <XCircle className="w-4 h-4" /> Batal
                      </button>
                    </>
                  )}
                  {bolehKelola && (it.status === "selesai" || it.status === "batal") && (
                    <button onClick={() => ubahStatus(it, "aktif")} disabled={busy}
                            data-testid={`po-buka-${it.id}`}
                            className="h-9 px-3 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 text-sm font-medium inline-flex items-center gap-1.5 disabled:opacity-50">
                      <RotateCcw className="w-4 h-4" /> Buka Lagi
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
          </div>
        ))}
        </LoadingState>
      </div>

      {/* Panel Katalog Barang PO — hanya pemegang preorder.update */}
      {bolehKelola && <KatalogPanel katalog={katalog} onReload={() => { loadKatalog(); loadEntri(); }} />}

      {/* Form tambah entri */}
      {form && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4"
             onClick={() => setForm(null)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
               onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <p className="font-display font-bold text-base text-slate-900">Tambah Pre-Order</p>
              <button onClick={() => setForm(null)} data-testid="po-cancel"
                      className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="text-xs font-medium text-slate-600">Barang PO <span className="text-rose-500">*</span></label>
                {/* Ganti katalog = kosongkan pilihan type: opsi milik katalog
                    lama tidak berlaku di katalog baru, dan meninggalkannya
                    membuat backend menolak dengan pesan yang membingungkan. */}
                <select value={form.item_id}
                        onChange={e => setForm(f => ({ ...f, item_id: e.target.value, type_dipilih: "" }))}
                        data-testid="po-f-item"
                        className="mt-1 h-10 w-full px-3 rounded-lg border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500">
                  <option value="">— pilih dari katalog —</option>
                  {katalogAktif.map(k => <option key={k.id} value={k.id}>{k.nama}</option>)}
                </select>
                {katalogAktif.length === 0 && (
                  <p className="text-[11px] text-amber-700 mt-1">
                    Belum ada barang aktif di katalog PO. {bolehKelola ? "Tambahkan dulu di panel Katalog Barang PO di bawah." : "Hubungi admin untuk menambahkannya."}
                  </p>
                )}
              </div>
              {/* Opsi "type" datang dari katalog yang dipilih (poin 1b) —
                  dropdown, bukan teks bebas, supaya rekap per opsi bisa
                  dijumlahkan. Katalog tanpa opsi tidak menampilkan apa pun. */}
              {(opsiKatalogTerpilih.length > 0) && (
                <div>
                  <label className="text-xs font-medium text-slate-600">Type <span className="text-rose-500">*</span></label>
                  <select value={form.type_dipilih || ""} data-testid="po-f-type"
                          onChange={e => setForm(f => ({ ...f, type_dipilih: e.target.value }))}
                          className="mt-1 h-10 w-full px-3 rounded-lg border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500">
                    <option value="">— pilih type —</option>
                    {opsiKatalogTerpilih.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
              )}
              <div>
                <label className="text-xs font-medium text-slate-600">Harga</label>
                <InputRupiah value={form.harga || ""} testid="po-f-harga"
                             onChange={n => setForm(f => ({ ...f, harga: n }))}
                             className="mt-1 h-10 w-full px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
              </div>
              {/* Trade-in di pre-order (poin 1c) hanya CATATAN rencana — unitnya
                  belum ada di tangan, jadi tidak menyentuh stok maupun jurnal.
                  Yang mengikat baru terjadi saat notanya dibuat. */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-600">Trade-in (unit pelanggan)</label>
                  <input value={form.trade_in_nama || ""} data-testid="po-f-tradein-nama"
                         onChange={e => setForm(f => ({ ...f, trade_in_nama: e.target.value }))}
                         placeholder="mis. iPhone 12 128GB"
                         className="mt-1 h-10 w-full px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600">Harga Trade-in</label>
                  <InputRupiah value={form.trade_in_harga || ""} testid="po-f-tradein-harga"
                               onChange={n => setForm(f => ({ ...f, trade_in_harga: n }))}
                               className="mt-1 h-10 w-full px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-600">Nama Pelanggan <span className="text-rose-500">*</span></label>
                  <input value={form.nama_pelanggan} data-testid="po-f-nama"
                         placeholder="cth. Budi Santoso"
                         onChange={e => setForm(f => ({ ...f, nama_pelanggan: e.target.value }))}
                         className="mt-1 h-10 w-full px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600">No. HP / WhatsApp</label>
                  <input value={form.no_hp} data-testid="po-f-hp" inputMode="tel"
                         placeholder="08xxxxxxxxxx"
                         onChange={e => setForm(f => ({ ...f, no_hp: e.target.value }))}
                         className="mt-1 h-10 w-full px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Cabang</label>
                <input value={form.cabang} list="po-branches" data-testid="po-f-cabang"
                       onChange={e => setForm(f => ({ ...f, cabang: e.target.value }))}
                       className="mt-1 h-10 w-full px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                <datalist id="po-branches">
                  {branches.map(b => <option key={b} value={b} />)}
                </datalist>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.dp} data-testid="po-f-dp"
                         onChange={e => setForm(f => ({ ...f, dp: e.target.checked }))}
                         className="w-4 h-4 accent-emerald-600" />
                  <span className="text-sm font-medium text-slate-700">Sudah bayar DP?</span>
                </label>
                {form.dp && (
                  <div className="mt-2">
                    <label className="text-xs font-medium text-slate-600">Nominal DP <span className="text-rose-500">*</span></label>
                    <InputRupiah value={form.dp_nominal || ""} testid="po-f-dp-nominal"
                                 onChange={n => setForm(f => ({ ...f, dp_nominal: n }))}
                                 className="mt-1 h-10 w-full px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                    <p className="text-[10px] text-slate-400 mt-1">
                      Ini catatan penjejakan saja — uangnya tetap wajib dicatat manual di Finance &gt; Catat Cepat.
                    </p>
                  </div>
                )}
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Catatan</label>
                <textarea value={form.catatan} rows={2} data-testid="po-f-catatan"
                          onChange={e => setForm(f => ({ ...f, catatan: e.target.value }))}
                          className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-100">
              <button onClick={() => setForm(null)}
                      className="h-10 px-4 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50">
                Batal
              </button>
              <button onClick={simpanEntri} disabled={busy} data-testid="po-simpan"
                      className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50">
                {busy ? "Menyimpan..." : "Simpan"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** Panel CRUD katalog barang PO — hanya tampil untuk pemegang preorder.update. */
function KatalogPanel({ katalog, onReload }) {
  const [nama, setNama] = useState("");
  const [busy, setBusy] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editNama, setEditNama] = useState("");
  const [editJudul, setEditJudul] = useState("");
  const [editOpsi, setEditOpsi] = useState("");
  const [opsiBaru, setOpsiBaru] = useState("");
  const [editBusy, setEditBusy] = useState(false);

  const tambah = async () => {
    const nm = nama.trim();
    if (!nm) { toast.error("Nama barang PO wajib diisi"); return; }
    setBusy(true);
    try {
      await api.post("/preorder/items", {
        nama: nm, aktif: true,
        // Opsi diketik dipisah koma — dipecah di sini supaya yang dikirim
        // sudah berupa larik, bukan satu teks panjang yang tak bisa direkap.
        opsi_type: opsiBaru.split(",").map(x => x.trim()).filter(Boolean),
      });
      setOpsiBaru("");
      toast.success(`Barang PO "${nm}" ditambahkan ke katalog`);
      setNama("");
      onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menambah barang PO");
    } finally { setBusy(false); }
  };

  const toggleAktif = async (it) => {
    try {
      await api.patch(`/preorder/items/${it.id}`, { aktif: !it.aktif });
      toast.success(`Barang PO "${it.nama}" ${it.aktif ? "dinonaktifkan" : "diaktifkan"}`);
      onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mengubah status barang PO");
    }
  };

  const mulaiEdit = (it) => {
    setEditId(it.id); setEditNama(it.nama);
    setEditJudul(it.judul || "");
    setEditOpsi((it.opsi_type || []).join(", "));
  };
  const batalEdit = () => { setEditId(null); setEditNama(""); setEditJudul(""); setEditOpsi(""); };

  const simpanEdit = async (it) => {
    const nm = editNama.trim();
    if (!nm) { toast.error("Nama tidak boleh kosong"); return; }
    setEditBusy(true);
    try {
      await api.patch(`/preorder/items/${it.id}`, {
        nama: nm, judul: editJudul.trim(),
        opsi_type: editOpsi.split(",").map(x => x.trim()).filter(Boolean),
      });
      toast.success("Nama barang PO diperbarui");
      batalEdit();
      onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memperbarui nama");
    } finally { setEditBusy(false); }
  };

  const hapus = async (it) => {
    if (!window.confirm(`Hapus barang PO "${it.nama}" dari katalog?`)) return;
    try {
      const r = await api.delete(`/preorder/items/${it.id}`);
      toast.success(r.data.message || "Barang PO dihapus dari katalog");
      onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menghapus barang PO");
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid="po-katalog-panel">
      <div className="p-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm font-semibold text-slate-900 inline-flex items-center gap-1.5">
          <Settings2 className="w-4 h-4 text-emerald-600" /> Katalog Barang PO ({katalog.length})
        </p>
        <div className="flex items-center gap-2">
          <input value={nama} onChange={e => setNama(e.target.value)} data-testid="po-item-nama"
                 placeholder="Nama barang PO baru"
                 onKeyDown={e => { if (e.key === "Enter") tambah(); }}
                 className="h-9 px-3 rounded-lg border border-slate-200 text-sm w-56 focus:outline-none focus:ring-2 focus:ring-emerald-500" />
          <input value={opsiBaru} onChange={e => setOpsiBaru(e.target.value)} data-testid="po-item-opsi"
                 placeholder="Opsi type, pisahkan koma"
                 onKeyDown={e => { if (e.key === "Enter") tambah(); }}
                 className="h-9 px-3 rounded-lg border border-slate-200 text-sm w-56 focus:outline-none focus:ring-2 focus:ring-emerald-500" />
          <button onClick={tambah} disabled={busy} data-testid="po-item-tambah"
                  className="h-9 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium inline-flex items-center gap-1.5 disabled:opacity-50">
            <Plus className="w-3.5 h-3.5" /> Tambah
          </button>
        </div>
      </div>
      <ul className="divide-y divide-slate-100">
        {katalog.map(it => (
          <li key={it.id} data-testid={`po-item-row-${it.id}`} className="p-3 flex items-center justify-between gap-2 flex-wrap">
            {editId === it.id ? (
              <div className="flex-1 min-w-[240px] space-y-1.5">
                <input value={editNama} onChange={e => setEditNama(e.target.value)}
                       data-testid={`po-item-input-${it.id}`} disabled={editBusy} placeholder="Nama barang"
                       onKeyDown={e => { if (e.key === "Enter") simpanEdit(it); if (e.key === "Escape") batalEdit(); }}
                       className="h-9 px-3 rounded-lg border border-emerald-300 text-sm w-full focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50" autoFocus />
                <input value={editJudul} onChange={e => setEditJudul(e.target.value)}
                       data-testid={`po-item-judul-${it.id}`} disabled={editBusy}
                       placeholder="Judul yang tampil di daftar (kosong = pakai nama)"
                       className="h-9 px-3 rounded-lg border border-slate-200 text-sm w-full focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50" />
                <input value={editOpsi} onChange={e => setEditOpsi(e.target.value)}
                       data-testid={`po-item-opsi-${it.id}`} disabled={editBusy}
                       placeholder="Opsi type, pisahkan koma — mis. 256GB, 512GB"
                       className="h-9 px-3 rounded-lg border border-slate-200 text-sm w-full focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50" />
              </div>
            ) : (
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-900">{it.nama}</p>
                {it.judul && it.judul !== it.nama && (
                  <p className="text-[11px] text-slate-500">Judul daftar: {it.judul}</p>
                )}
                {(it.opsi_type || []).length > 0 && (
                  <div className="flex gap-1 flex-wrap mt-1">
                    {it.opsi_type.map(o => (
                      <span key={o} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{o}</span>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div className="flex items-center gap-1.5">
              {editId === it.id ? (
                <>
                  <button onClick={() => simpanEdit(it)} disabled={editBusy} data-testid={`po-item-simpan-${it.id}`}
                          className="h-8 px-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium inline-flex items-center gap-1 disabled:opacity-50">
                    <Check className="w-3.5 h-3.5" /> Simpan
                  </button>
                  <button onClick={batalEdit} disabled={editBusy} data-testid={`po-item-batal-${it.id}`}
                          className="h-8 px-2.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 text-xs font-medium disabled:opacity-50">
                    Batal
                  </button>
                </>
              ) : (
                <>
                  <button onClick={() => toggleAktif(it)} data-testid={`po-item-toggle-${it.id}`}
                          className={`px-2 py-1 rounded-full text-xs font-medium ${it.aktif ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                    {it.aktif ? "Aktif" : "Nonaktif"}
                  </button>
                  <button onClick={() => mulaiEdit(it)} data-testid={`po-item-edit-${it.id}`}
                          className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-slate-700">
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => hapus(it)} data-testid={`po-item-hapus-${it.id}`}
                          className="p-1.5 hover:bg-rose-50 rounded-lg text-slate-400 hover:text-rose-600">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </>
              )}
            </div>
          </li>
        ))}
        {katalog.length === 0 && (
          <li className="p-6 text-center text-sm text-slate-500">Belum ada barang di katalog PO. Tambahkan lewat form di atas.</li>
        )}
      </ul>
    </div>
  );
}
